import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2, Save, X } from 'lucide-react';
import adminApi from '../../api/adminApi';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { useToast } from '../../hooks/use-toast';

const AdminServices = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingService, setEditingService] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const { toast } = useToast();

  const emptyService = {
    title: '',
    description: '',
    icon: 'Video',
    features: ['', '', '']
  };

  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    try {
      const data = await adminApi.getServices();
      setServices(data);
    } catch (error) {
      // Services might not exist yet
      setServices([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (serviceData) => {
    try {
      if (serviceData.id) {
        await adminApi.updateService(serviceData.id, serviceData);
        toast({ title: "Success", description: "Service updated" });
      } else {
        await adminApi.createService(serviceData);
        toast({ title: "Success", description: "Service created" });
      }
      setEditingService(null);
      setIsCreating(false);
      loadServices();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save service",
        variant: "destructive"
      });
    }
  };

  const handleDelete = async (serviceId) => {
    if (!window.confirm('Delete this service?')) return;
    
    try {
      await adminApi.deleteService(serviceId);
      toast({ title: "Success", description: "Service deleted" });
      loadServices();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete service",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return <div className="text-center py-20">Loading...</div>;
  }

  if (isCreating || editingService) {
    return (
      <ServiceForm
        service={editingService || emptyService}
        onSave={handleSave}
        onCancel={() => {
          setEditingService(null);
          setIsCreating(false);
        }}
      />
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-black uppercase mb-2">Services</h1>
          <p className="text-gray-600">Manage the services you offer</p>
        </div>
        <Button
          onClick={() => setIsCreating(true)}
          className="bg-black text-white hover:bg-gray-900 font-bold"
        >
          <Plus className="mr-2" size={18} />
          Add Service
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {services.map((service, index) => (
          <motion.div
            key={service.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            className="bg-white p-6 rounded-lg border-2 border-gray-200 hover:border-black transition-colors group"
          >
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-xl font-black uppercase">{service.title}</h3>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => setEditingService(service)}
                  className="bg-black text-white hover:bg-gray-800"
                >
                  <Edit size={14} />
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleDelete(service.id)}
                  className="bg-red-600 text-white hover:bg-red-700"
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
            <p className="text-gray-600 text-sm mb-4">{service.description}</p>
            <div className="space-y-1">
              {service.features.map((feature, idx) => (
                <div key={idx} className="text-xs text-gray-500 font-semibold">
                  • {feature}
                </div>
              ))}
            </div>
          </motion.div>
        ))}
      </div>

      {services.length === 0 && (
        <div className="text-center py-20 text-gray-500">
          <p>No services yet. Click "Add Service" to get started!</p>
        </div>
      )}
    </div>
  );
};

const ServiceForm = ({ service, onSave, onCancel }) => {
  const [formData, setFormData] = useState(service);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFeatureChange = (index, value) => {
    const newFeatures = [...formData.features];
    newFeatures[index] = value;
    setFormData(prev => ({ ...prev, features: newFeatures }));
  };

  const addFeature = () => {
    setFormData(prev => ({ ...prev, features: [...prev.features, ''] }));
  };

  const removeFeature = (index) => {
    const newFeatures = formData.features.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, features: newFeatures }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-4xl font-black uppercase">
          {service.id ? 'Edit Service' : 'New Service'}
        </h1>
        <Button
          onClick={onCancel}
          variant="outline"
          className="border-2 border-black"
        >
          <X className="mr-2" size={18} />
          Cancel
        </Button>
      </div>

      <form onSubmit={handleSubmit} className="bg-white p-8 rounded-lg border-2 border-gray-200 max-w-2xl">
        <div className="space-y-6">
          <div>
            <Label className="text-xs font-bold uppercase mb-2 block">Service Title *</Label>
            <Input
              required
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              className="border-2 border-black h-12 font-black"
              placeholder="BRAND FILMS"
            />
          </div>

          <div>
            <Label className="text-xs font-bold uppercase mb-2 block">Description *</Label>
            <Textarea
              required
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              className="border-2 border-black"
              rows={3}
              placeholder="Describe this service..."
            />
          </div>

          <div>
            <Label className="text-xs font-bold uppercase mb-2 block">Icon Name</Label>
            <Input
              value={formData.icon}
              onChange={(e) => handleChange('icon', e.target.value)}
              className="border-2 border-black h-12"
              placeholder="Video, Film, Mic, Home, Users, Smartphone"
            />
            <p className="text-xs text-gray-500 mt-1">Use: Video, Film, Mic, Home, Users, or Smartphone</p>
          </div>

          <div>
            <Label className="text-xs font-bold uppercase mb-2 block">Features</Label>
            <div className="space-y-2">
              {formData.features.map((feature, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    value={feature}
                    onChange={(e) => handleFeatureChange(index, e.target.value)}
                    className="border-2 border-black h-10"
                    placeholder={`Feature ${index + 1}`}
                  />
                  {formData.features.length > 1 && (
                    <Button
                      type="button"
                      onClick={() => removeFeature(index)}
                      size="sm"
                      className="bg-red-600 text-white hover:bg-red-700"
                    >
                      <X size={16} />
                    </Button>
                  )}
                </div>
              ))}
            </div>
            <Button
              type="button"
              onClick={addFeature}
              size="sm"
              className="mt-2 bg-gray-200 text-black hover:bg-gray-300"
            >
              <Plus size={16} className="mr-1" />
              Add Feature
            </Button>
          </div>
        </div>

        <div className="mt-8 flex justify-end gap-4">
          <Button
            type="button"
            onClick={onCancel}
            variant="outline"
            className="border-2 border-gray-300"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="bg-black text-white hover:bg-gray-900 font-bold"
          >
            <Save className="mr-2" size={18} />
            Save Service
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AdminServices;